import { h, Component } from 'preact';
import style from './style';
import root from '@components/root';
import { slugify } from '@helpers';
import find from 'dlv';

const interactionObserverSupported = 'IntersectionObserver' in root;

export const Tab = props => (
  <div
    class={style.tab}
    aria-labelledby={`${slugify(props.tab)}Anchor`}
    aria-hidden={!props.selected}
    id={`${slugify(props.tab)}`}
  >
    {props.selected ? props.children : null}
  </div>
);

export class Tabs extends Component {
  state = {
    selected: this.props.selected || 0,
    external: this.props.external,
  };
  runningScrolls = 0;
  getHash = () =>
    window.location.hash ? window.location.hash.substring(1) : null;
  interactionHandler = entries => {
    if (!entries[0].isIntersecting) {
      this.props.onToggleSticky(true);
    } else {
      this.props.onToggleSticky(false);
    }
  };
  initObserver = () => {
    if (
      interactionObserverSupported &&
      this.props.onToggleSticky &&
      this.base.firstChild
    ) {
      if (this.observer) this.observer.disconnect();
      this.observer = new root.IntersectionObserver(this.interactionHandler);
      this.observer.observe(this.base.firstChild);
    }
  };
  initialScroll = props => {
    const hash = this.getHash();
    if (hash) {
      props.children
        .filter(item => item)
        .forEach((item, index) => {
          if (
            slugify(find(item || {}, 'attributes.tab', '')) === hash ||
            (item.attributes.external &&
              new URL(find(item || {}, 'attributes.external', '')).host ===
                hash)
          ) {
            this.setState({ external: item.attributes.external });
            this.runningScrolls = this.runningScrolls + 1;
            window.setTimeout(() => {
              if (this.runningScrolls === 1)
                this.selectIndex(index, item.attributes.external);
              this.runningScrolls = this.runningScrolls - 1;
            }, 32);
          }
        });
    }
  };
  componentDidMount() {
    this.initObserver();
    this.initialScroll(this.props);
  }
  componentWillReceiveProps(nextProps) {
    if (!this.observer) {
      this.initObserver();
    }
    if (
      nextProps.children.filter(item => item).length !==
      this.props.children.filter(item => item).length
    ) {
      this.initialScroll(nextProps);
    }
  }
  componentWillUnmount() {
    if (this.observer) this.observer.disconnect();
  }

  inView = rect => {
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <=
        (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  };

  selectIndex = (index, external) =>
    this.setState({ selected: index }, () => {
      const rect =
        this.base.children[external ? 0 : index + 2].getBoundingClientRect();
      if (!this.inView(rect) || external) {
        root.scrollBy({
          top: rect.top - this.base.firstChild.clientHeight,
          behavior: 'smooth',
        });
        window.history.pushState(
          {},
          '',
          `${window.location.pathname}#${
            this.base.children[external ? 2 : index + 2].id
          }`
        );
      }

      if (
        typeof this.props.children.filter(item => item)[index].attributes
          .onSelect === 'function'
      ) {
        typeof this.props.children
          .filter(item => item)
          [index].attributes.onSelect(
            typeof this.props.children.filter(item => item)[index].attributes
              .tab
          );
      }
    });
  render(props, { selected }) {
    return (
      <div class={style.tabs}>
        <span class={style.watchTarget} />
        {props.hideTabs === true ? null : (
          <div
            class={`${style.tablist}${
              props.transparent ? ` ${style.transparent}` : ''
            }${props.inverse ? ` ${style.inverse}` : ''} ${
              props.unsticky ? ` ${style.unsticky}` : ''
            }`.trim()}
            style={props.style}
            role="tablist"
            aria-label={props.label}
          >
            <div class={'contain ' + style.tabLabelWrapper}>
              <div class={style.regularTabs}>
                {props.children
                  .filter(item => item)
                  .map((tab, index) => (
                    <button
                      role="tab"
                      aria-selected={selected === index ? 'true' : 'false'}
                      aria-controls={`${slugify(tab.attributes.tab)}`}
                      id={`${slugify(tab.attributes.tab)}Anchor`}
                      onClick={() => {
                        this.setState({ external: tab.attributes.external });
                        this.selectIndex(index, tab.attributes.external);
                      }}
                    >
                      {tab.attributes.tab}
                    </button>
                  ))}
              </div>
              {props.extraTabs ? (
                <div class={style.mobileMenu}>
                  <select
                    onChange={e => this.selectIndex(e.target.selectedIndex)}
                  >
                    {props.children
                      .filter(item => item)
                      .map(tab => (
                        <option value={slugify(tab.attributes.tab)}>
                          {tab.attributes.tab}
                        </option>
                      ))}
                  </select>

                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    viewBox="0 0 24 24"
                  >
                    <path d="M24 6h-24v-4h24v4zm0 4h-24v4h24v-4zm0 8h-24v4h24v-4z" />
                  </svg>
                </div>
              ) : null}
              <div class={style.mobileTabs}>
                <select
                  onChange={e => this.selectIndex(e.target.selectedIndex)}
                >
                  {props.children
                    .filter(item => item)
                    .map(tab => (
                      <option value={slugify(tab.attributes.tab)}>
                        {tab.attributes.tab}
                      </option>
                    ))}
                </select>

                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="12"
                  viewBox="0 0 24 24"
                >
                  <path d="M0 7.33l2.829-2.83 9.175 9.339 9.167-9.339 2.829 2.83-11.996 12.17z"></path>
                </svg>
              </div>

              <div class={style.extraTabs}>{props.extraTabs}</div>
            </div>
          </div>
        )}
        {this.state.external ? (
          <iframe
            id={new URL(this.state.external).host}
            src={this.state.external}
            class={style.externalTab}
          />
        ) : (
          props.children
            .filter(item => item)
            .map((item, index) => {
              item.attributes.selected = props.showAll || index === selected;
              return item;
            })
        )}
      </div>
    );
  }
}

export default { Tabs, Tab };



// WEBPACK FOOTER //
// ./components/tabs/index.js